import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  GearSix,
  Lightbulb,
  ClockCounterClockwise,
  Plus,
  Check,
  X,
  Play,
  Eye,
  ChatCircle,
  Lightning,
  Clock,
  Warning,
  ArrowsClockwise,
  Trash,
  ShieldCheck,
} from '@phosphor-icons/react';
import { AdminSSOTester } from '../components/AdminSSOTester';

export const AdminPage = () => {
  const { api } = useAuth();
  const { openCopilot } = useCopilot();
  const [activeTab, setActiveTab] = useState('users');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editRolesOpen, setEditRolesOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    department: '',
    roles: ['EMPLOYEE'],
  });
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Proposed Learnings state
  const [proposedLearnings, setProposedLearnings] = useState([]);
  const [loadingPL, setLoadingPL] = useState(true);
  const [plFilter, setPlFilter] = useState('all');

  // Config state
  const [configs, setConfigs] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Audit Logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(true);

  // Clarifications state
  const [clarifications, setClarifications] = useState([]);
  const [loadingCl, setLoadingCl] = useState(true);

  // Forced Fill state
  const [pendingAcks, setPendingAcks] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [ackConfig, setAckConfig] = useState({
    ack_deadline_minutes: 30,
    escalation_enabled: true,
    escalation_levels: [],
    auto_reassign_enabled: true,
    max_declines_per_window: 1
  });
  const [loadingFF, setLoadingFF] = useState(true);
  const [editTimeoutOpen, setEditTimeoutOpen] = useState(false);
  const [newTimeout, setNewTimeout] = useState(30);
  const [editEscalationOpen, setEditEscalationOpen] = useState(false);
  const [newEscalationLevels, setNewEscalationLevels] = useState([]);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'learnings') fetchProposedLearnings();
    else if (activeTab === 'config') fetchConfigs();
    else if (activeTab === 'audit') fetchAuditLogs();
    else if (activeTab === 'clarifications') fetchClarifications();
    else if (activeTab === 'forcedfill') fetchForcedFillData();
  }, [activeTab, plFilter]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchProposedLearnings = async () => {
    setLoadingPL(true);
    try {
      let url = '/admin/proposed-learnings';
      if (plFilter !== 'all') {
        url += `?status=${plFilter}`;
      }
      const response = await api.get(url);
      setProposedLearnings(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch proposed learnings');
    } finally {
      setLoadingPL(false);
    }
  };

  const fetchConfigs = async () => {
    setLoadingConfig(true);
    try {
      const response = await api.get('/config');
      setConfigs(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch configs');
    } finally {
      setLoadingConfig(false);
    }
  };

  const fetchAuditLogs = async () => {
    setLoadingAudit(true);
    try {
      const response = await api.get('/admin/audit-logs');
      setAuditLogs(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoadingAudit(false);
    }
  };

  const fetchClarifications = async () => {
    setLoadingCl(true);
    try {
      const response = await api.get('/admin/clarifications');
      setClarifications(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch clarifications');
    } finally {
      setLoadingCl(false);
    }
  };

  const fetchForcedFillData = async () => {
    setLoadingFF(true);
    try {
      const [acksRes, escalationsRes, configRes] = await Promise.all([
        api.get('/admin/canvass/pending-acknowledgements'),
        api.get('/admin/canvass/escalations'),
        api.get('/admin/canvass/ack-config')
      ]);
      setPendingAcks(acksRes.data || []);
      setEscalations(escalationsRes.data || []);
      if (configRes.data) {
        setAckConfig(configRes.data);
        setNewTimeout(configRes.data.ack_deadline_minutes);
        setNewEscalationLevels(configRes.data.escalation_levels || []);
      }
    } catch (error) {
      console.error('Failed to fetch forced fill data:', error);
    } finally {
      setLoadingFF(false);
    }
  };

  const handleUpdateTimeout = async () => {
    try {
      await api.put('/admin/canvass/ack-config/timeout', { minutes: newTimeout });
      toast.success('Timeout updated');
      setEditTimeoutOpen(false);
      fetchForcedFillData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timeout');
    }
  };

  const handleUpdateEscalationLevels = async () => {
    try {
      await api.put('/admin/canvass/ack-config/escalation-levels', { levels: newEscalationLevels });
      toast.success('Escalation levels updated');
      setEditEscalationOpen(false);
      fetchForcedFillData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update escalation levels');
    }
  };

  const handleToggleAutoReassign = async (enabled) => {
    try {
      await api.put(`/admin/canvass/ack-config/auto-reassign?enabled=${enabled}`);
      toast.success(`Auto-reassign ${enabled ? 'enabled' : 'disabled'}`);
      fetchForcedFillData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle auto-reassign');
    }
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expired';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addEscalationLevel = () => {
    setNewEscalationLevels([...newEscalationLevels, { afterMinutes: 10, notifyRole: 'SUPERVISOR' }]);
  };

  const removeEscalationLevel = (index) => {
    setNewEscalationLevels(newEscalationLevels.filter((_, i) => i !== index));
  };

  const updateEscalationLevel = (index, field, value) => {
    const updated = [...newEscalationLevels];
    updated[index] = { ...updated[index], [field]: field === 'afterMinutes' ? parseInt(value) : value };
    setNewEscalationLevels(updated);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', newUser);
      toast.success('User created successfully');
      setCreateUserOpen(false);
      setNewUser({ name: '', email: '', password: '', department: '', roles: ['EMPLOYEE'] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateRoles = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/admin/users/${selectedUser.id}/roles`, { roles: selectedRoles });
      toast.success('Roles updated successfully');
      setEditRolesOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roles');
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}`, { status });
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleApprovePL = async (plId) => {
    try {
      await api.put(`/admin/proposed-learnings/${plId}/approve`);
      toast.success('Learning approved');
      fetchProposedLearnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectPL = async (plId) => {
    try {
      await api.put(`/admin/proposed-learnings/${plId}/reject`);
      toast.success('Learning rejected');
      fetchProposedLearnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleApplyPL = async (plId) => {
    try {
      await api.post(`/admin/proposed-learnings/${plId}/apply`);
      toast.success('Learning applied');
      fetchProposedLearnings();
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    }
  };

  // Ask Agent prompts for admin
  const learningsAgentPrompts = [
    { label: 'Recommend Approval', prompt: 'Analyze the proposed learnings and recommend which ones should be approved based on impact and risk.' },
    { label: 'Spot Duplicates', prompt: 'Check for duplicate or similar proposed learnings that could be consolidated.' },
  ];

  const auditAgentPrompts = [
    { label: 'Summarize Last 7 Days', prompt: 'Summarize the audit log activity from the last 7 days, highlighting any unusual patterns.' },
  ];

  const openEditRoles = (user) => {
    setSelectedUser(user);
    setSelectedRoles(user.roles || []);
    setEditRolesOpen(true);
  };

  const toggleRole = (roleName) => {
    setSelectedRoles(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-slate-100 text-slate-800">Inactive</Badge>;
      case 'submitted':
        return <Badge className="bg-amber-100 text-amber-800">Submitted</Badge>;
      case 'approved':
        return <Badge className="bg-blue-100 text-blue-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      case 'applied':
        return <Badge className="bg-emerald-100 text-emerald-800">Applied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
          Admin Panel
        </h1>
        <p className="text-slate-600">
          Manage users, roles, configurations, and view system logs
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users size={16} /> Users
          </TabsTrigger>
          <TabsTrigger value="forcedfill" className="gap-2" data-testid="tab-forcedfill">
            <Lightning size={16} /> Forced Fill
          </TabsTrigger>
          <TabsTrigger value="learnings" className="gap-2" data-testid="tab-learnings">
            <Lightbulb size={16} /> Learnings
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2" data-testid="tab-config">
            <GearSix size={16} /> Config
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit">
            <ClockCounterClockwise size={16} /> Audit Logs
          </TabsTrigger>
          <TabsTrigger value="clarifications" className="gap-2" data-testid="tab-clarifications">
            <ChatCircle size={16} /> Clarifications
          </TabsTrigger>
          <TabsTrigger value="sso" className="gap-2" data-testid="tab-sso">
            <ShieldCheck size={16} /> SSO
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-display">Users & Roles</CardTitle>
                <CardDescription>Manage user accounts and their permissions</CardDescription>
              </div>
              <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary" data-testid="create-user-btn">
                    <Plus size={18} className="mr-2" /> Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        required
                        data-testid="new-user-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                        data-testid="new-user-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        data-testid="new-user-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Input
                        value={newUser.department}
                        onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                        data-testid="new-user-department"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Roles</Label>
                      <div className="flex flex-wrap gap-2">
                        {roles.map((role) => (
                          <label key={role.id} className="flex items-center gap-2">
                            <Checkbox
                              checked={newUser.roles.includes(role.name)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setNewUser({ ...newUser, roles: [...newUser.roles, role.name] });
                                } else {
                                  setNewUser({ ...newUser, roles: newUser.roles.filter(r => r !== role.name) });
                                }
                              }}
                            />
                            <span className="text-sm">{role.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-primary" data-testid="submit-new-user">
                        Create User
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.department || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <Badge key={role} variant="outline" className="text-xs">
                                {role}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditRoles(user)}
                              data-testid={`edit-roles-${user.id}`}
                            >
                              Edit Roles
                            </Button>
                            {user.status === 'active' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateUserStatus(user.id, 'inactive')}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateUserStatus(user.id, 'active')}
                              >
                                Activate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Edit Roles Dialog */}
          <Dialog open={editRolesOpen} onOpenChange={setEditRolesOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Roles for {selectedUser?.name}</DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-3">
                {roles.map((role) => (
                  <label key={role.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                    <Checkbox
                      checked={selectedRoles.includes(role.name)}
                      onCheckedChange={() => toggleRole(role.name)}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={handleUpdateRoles} className="bg-primary" data-testid="save-roles-btn">
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Proposed Learnings Tab */}
        <TabsContent value="learnings">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display">Proposed Learnings</CardTitle>
                  <CardDescription>Review and manage AI-proposed learnings and configurations</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <AskAgentButton
                    prompts={learningsAgentPrompts}
                    onPromptSelect={(prompt) => openCopilot(prompt)}
                  />
                  <Select value={plFilter} onValueChange={setPlFilter}>
                    <SelectTrigger className="w-40" data-testid="pl-filter">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="applied">Applied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPL ? (
                <div className="text-center py-8">Loading...</div>
              ) : proposedLearnings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No proposed learnings</div>
              ) : (
                <div className="space-y-4">
                  {proposedLearnings.map((pl) => (
                    <div key={pl.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`pl-${pl.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-medium">{pl.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <Badge variant="outline">{pl.type}</Badge>
                            <Badge variant="outline">{pl.module}</Badge>
                            <Badge variant="outline">Priority: {pl.priority}</Badge>
                            {pl.proposed_by_agent && (
                              <Badge className="bg-violet-100 text-violet-800">AI Proposed</Badge>
                            )}
                          </div>
                        </div>
                        {getStatusBadge(pl.status)}
                      </div>
                      {pl.reason && (
                        <p className="text-sm text-slate-600 mb-2"><strong>Reason:</strong> {pl.reason}</p>
                      )}
                      {pl.expected_impact && (
                        <p className="text-sm text-slate-600 mb-2"><strong>Expected Impact:</strong> {pl.expected_impact}</p>
                      )}
                      {pl.proposed_key && (
                        <div className="text-sm font-mono bg-slate-50 p-2 rounded mb-2">
                          <span className="text-slate-500">Key:</span> {pl.proposed_key}
                          <br />
                          <span className="text-slate-500">Value:</span> {JSON.stringify(pl.proposed_value_json)}
                        </div>
                      )}
                      {pl.status === 'submitted' && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprovePL(pl.id)}
                            data-testid={`approve-pl-${pl.id}`}
                          >
                            <Check size={16} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleRejectPL(pl.id)}
                            data-testid={`reject-pl-${pl.id}`}
                          >
                            <X size={16} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {pl.status === 'approved' && pl.type === 'config_change' && (
                        <Button
                          size="sm"
                          className="mt-3 bg-primary"
                          onClick={() => handleApplyPL(pl.id)}
                          data-testid={`apply-pl-${pl.id}`}
                        >
                          <Play size={16} className="mr-1" /> Apply Config
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-display">System Configuration</CardTitle>
              <CardDescription>View current system configuration values</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingConfig ? (
                <div className="text-center py-8">Loading...</div>
              ) : configs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No configurations</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Key</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {configs.map((config) => (
                      <TableRow key={config.id} data-testid={`config-${config.id}`}>
                        <TableCell className="font-mono text-sm">{config.key}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {JSON.stringify(config.value_json)}
                          </code>
                        </TableCell>
                        <TableCell>{config.scope}</TableCell>
                        <TableCell>v{config.version}</TableCell>
                        <TableCell>{getStatusBadge(config.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display">Audit Logs</CardTitle>
                  <CardDescription>View system activity and changes</CardDescription>
                </div>
                <AskAgentButton
                  prompts={auditAgentPrompts}
                  onPromptSelect={(prompt) => openCopilot(prompt)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loadingAudit ? (
                <div className="text-center py-8">Loading...</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No audit logs</div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Actor</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`audit-${log.id}`}>
                          <TableCell className="text-sm">
                            {new Date(log.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.actor_type}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{log.action}</TableCell>
                          <TableCell>{log.target_type}</TableCell>
                          <TableCell>
                            {log.reason && (
                              <span className="text-sm text-slate-500">{log.reason}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clarifications Tab */}
        <TabsContent value="clarifications">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-display">Clarification Logs</CardTitle>
              <CardDescription>View AI clarification question and answer history</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingCl ? (
                <div className="text-center py-8">Loading...</div>
              ) : clarifications.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No clarifications logged</div>
              ) : (
                <div className="space-y-4">
                  {clarifications.map((cl) => (
                    <div key={cl.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`cl-${cl.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{cl.module}</Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(cl.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm"><strong>Q:</strong> {cl.question}</p>
                      <p className="text-sm text-slate-600 mt-1"><strong>A:</strong> {cl.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forced Fill Tab */}
        <TabsContent value="forcedfill">
          <div className="space-y-6">
            {/* Config Section */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <GearSix size={20} />
                  Acknowledgement Settings
                </CardTitle>
                <CardDescription>Configure timeouts and escalation rules</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingFF ? (
                  <div className="text-center py-4">Loading...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Timeout Setting */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock size={18} className="text-slate-500" />
                          <span className="font-medium">Timeout</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setEditTimeoutOpen(true)}>
                          Edit
                        </Button>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{ackConfig.ack_deadline_minutes} min</p>
                      <p className="text-sm text-slate-500">Time to acknowledge</p>
                    </div>

                    {/* Auto Reassign */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ArrowsClockwise size={18} className="text-slate-500" />
                          <span className="font-medium">Auto-Reassign</span>
                        </div>
                        <Switch 
                          checked={ackConfig.auto_reassign_enabled}
                          onCheckedChange={handleToggleAutoReassign}
                          data-testid="auto-reassign-switch"
                        />
                      </div>
                      <p className="text-sm text-slate-500">
                        {ackConfig.auto_reassign_enabled ? 'Automatically reassign on decline/expire' : 'Manual reassignment only'}
                      </p>
                    </div>

                    {/* Max Declines */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <X size={18} className="text-slate-500" />
                        <span className="font-medium">Max Declines</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{ackConfig.max_declines_per_window}</p>
                      <p className="text-sm text-slate-500">Per canvass window</p>
                    </div>

                    {/* Escalation Levels */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Warning size={18} className="text-slate-500" />
                          <span className="font-medium">Escalation</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setEditEscalationOpen(true)}>
                          Edit
                        </Button>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{ackConfig.escalation_levels?.length || 0}</p>
                      <p className="text-sm text-slate-500">Escalation levels</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Acknowledgements */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Clock size={20} />
                  Pending Acknowledgements
                  {pendingAcks.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-800">{pendingAcks.length}</Badge>
                  )}
                </CardTitle>
                <CardDescription>Assignments awaiting employee acknowledgement</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingFF ? (
                  <div className="text-center py-8">Loading...</div>
                ) : pendingAcks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No pending acknowledgements</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Time Remaining</TableHead>
                        <TableHead>Escalated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingAcks.map((ack) => (
                        <TableRow key={ack.id} data-testid={`pending-ack-${ack.id}`}>
                          <TableCell>
                            <div>
                              <span className="font-medium">{ack.user_name}</span>
                              <p className="text-xs text-slate-500">{ack.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{ack.job_title}</TableCell>
                          <TableCell>{ack.crew_role_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {ack.assignment_source?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={`font-mono ${ack.time_remaining && ack.time_remaining < 300 ? 'text-red-600 font-bold' : ''}`}>
                              {formatTimeRemaining(ack.time_remaining)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {ack.escalated_levels?.length > 0 ? (
                              <Badge className="bg-orange-100 text-orange-800">
                                Level {ack.escalated_levels.length}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Recent Escalations */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Warning size={20} />
                  Recent Escalations & Alerts
                </CardTitle>
                <CardDescription>Escalation notifications and declined/expired assignments</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingFF ? (
                  <div className="text-center py-8">Loading...</div>
                ) : escalations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No recent escalations</div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {escalations.map((esc) => (
                      <div 
                        key={esc.id} 
                        className={`p-4 rounded-lg border ${
                          esc.type === 'ASSIGNMENT_EXPIRED' ? 'bg-red-50 border-red-200' :
                          esc.type === 'ASSIGNMENT_DECLINED' ? 'bg-amber-50 border-amber-200' :
                          'bg-orange-50 border-orange-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={
                                esc.type === 'ASSIGNMENT_EXPIRED' ? 'bg-red-100 text-red-800' :
                                esc.type === 'ASSIGNMENT_DECLINED' ? 'bg-amber-100 text-amber-800' :
                                'bg-orange-100 text-orange-800'
                              }>
                                {esc.type?.replace(/_/g, ' ')}
                              </Badge>
                              {esc.priority === 'high' && (
                                <Badge className="bg-red-100 text-red-800">HIGH</Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-slate-900">{esc.title}</h4>
                            <p className="text-sm text-slate-600">{esc.message}</p>
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {new Date(esc.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Edit Timeout Dialog */}
          <Dialog open={editTimeoutOpen} onOpenChange={setEditTimeoutOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Acknowledgement Timeout</DialogTitle>
                <DialogDescription>
                  Set how long employees have to acknowledge their assignments
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label>Timeout (minutes)</Label>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={newTimeout}
                  onChange={(e) => setNewTimeout(parseInt(e.target.value) || 30)}
                  className="mt-2"
                  data-testid="timeout-input"
                />
                <p className="text-sm text-slate-500 mt-2">
                  Min: 5 minutes, Max: 24 hours (1440 minutes)
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditTimeoutOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateTimeout} data-testid="save-timeout-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Escalation Levels Dialog */}
          <Dialog open={editEscalationOpen} onOpenChange={setEditEscalationOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Escalation Levels</DialogTitle>
                <DialogDescription>
                  Configure when and who to notify as time passes
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                {newEscalationLevels.map((level, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs">After (minutes)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={level.afterMinutes}
                        onChange={(e) => updateEscalationLevel(index, 'afterMinutes', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Notify Role</Label>
                      <Select value={level.notifyRole} onValueChange={(v) => updateEscalationLevel(index, 'notifyRole', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SUPERVISOR">Supervisor</SelectItem>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeEscalationLevel(index)}>
                      <Trash size={16} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={addEscalationLevel} className="w-full">
                  <Plus size={16} className="mr-2" /> Add Level
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditEscalationOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateEscalationLevels} data-testid="save-escalation-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* SSO Tab */}
        <TabsContent value="sso">
          <div className="grid gap-6 md:grid-cols-2">
            <AdminSSOTester />
            
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">SSO Integration Guide</CardTitle>
                <CardDescription>
                  Steps to integrate SSO with your external applications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-slate-600">
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">1. Get Client Credentials</h4>
                  <p>Create an OIDC client in the admin panel and save the client_id and client_secret.</p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">2. Use the Client Template</h4>
                  <p>
                    Copy the template from{' '}
                    <code className="bg-slate-100 px-1 rounded text-xs">/packages/oidc-sso-client-template/</code>
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">3. Configure Environment Variables</h4>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li><code>OIDC_ISSUER</code> - GETGRIDGATE URL</li>
                    <li><code>OIDC_CLIENT_ID</code> - Your client ID</li>
                    <li><code>OIDC_CLIENT_SECRET</code> - Your client secret</li>
                    <li><code>OIDC_REDIRECT_URI</code> - Your callback URL</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">4. Test the Flow</h4>
                  <p>Use the "Test SSO Flow" card to verify the integration works correctly.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
